import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Play } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/lib/i18n";
import { useProducts } from "@/lib/useProducts";
import { api } from "@/lib/api";
import { CATEGORY_META, GRADIENTS } from "@/lib/meta";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Placeholder from "@/components/Placeholder";
import ProductCard from "@/components/ProductCard";
import Marquee from "@/components/Marquee";

export default function Home() {
  const { t, lang, isRtl } = useLang();
  const { data: products = [] } = useProducts();
  const heroRef = useRef(null);
  const scroller = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 50]);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const newIn = products.filter((p) => p.tag === "new").slice(0, 8);
  const grid = newIn.length >= 4 ? newIn : products.slice(0, 8);
  const videoTiles = products.slice(0, 6);
  const cats = ["robes", "hauts", "jupes", "manteaux"];
  const homeFaq = ["orders", "shipping", "returns", "sizing", "payment"].map((c) => ({
    cat: c,
    item: t.faq.items[c][0],
  }));

  const scrollVideos = () => {
    const el = scroller.current;
    if (!el) return;
    const w = el.clientWidth * 0.7;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 30;
    if (atEnd) el.scrollTo({ left: 0, behavior: "smooth" });
    else el.scrollBy({ left: isRtl ? -w : w, behavior: "smooth" });
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    setSubscribing(true);
    try {
      await api.post("/newsletter", { email });
      toast.success(t.home.newsletter.ok);
      setEmail("");
    } catch {
      toast.error(t.home.newsletter.err);
    }
    setSubscribing(false);
  };

  return (
    <div data-testid="home-page">
      {/* ————— HERO — rounded visual with bold overlay ————— */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="home-hero">
        <div className="max-w-[1440px] mx-auto px-3 sm:px-5 lg:px-8 pt-4 sm:pt-6">
          <div className="relative rounded-[1.6rem] sm:rounded-[2rem] overflow-hidden max-w-[1360px] mx-auto">
            <motion.div style={{ y: yImg }}>
              <Placeholder variant={1} label="" aspect="aspect-[4/5] sm:aspect-[16/11] lg:aspect-[21/9]" />
            </motion.div>
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink/65 via-ink/25 to-transparent pointer-events-none" />
            <div className="absolute bottom-7 start-6 sm:bottom-12 sm:start-12 end-6 z-10">
              <h1 className="font-sans font-medium uppercase text-ivory text-[clamp(1.7rem,4.2vw,3.4rem)] leading-[1.08] tracking-[0.04em]">
                <MaskedLine text={t.hero.line1} delay={0.25} />
                <MaskedLine text={t.hero.line2} delay={0.4} />
              </h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="mt-3 sm:mt-4 text-[10.5px] sm:text-[11px] uppercase tracking-[0.22em] text-ivory/80"
              >
                {t.hero.caption}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.95, duration: 0.8 }}
                className="mt-6 sm:mt-8"
              >
                <Link to="/boutique" data-testid="hero-cta-collection" className="btn bg-ivory text-ink hover:bg-rose hover:text-ivory">
                  {t.hero.cta1}
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="hidden lg:flex absolute bottom-5 left-1/2 -translate-x-1/2 flex-col items-center gap-1 text-ink/35"
        >
          <span className="text-[9.5px] uppercase tracking-[0.22em]">{t.hero.scroll}</span>
          <ChevronDown className="w-4 h-4 animate-bounce" strokeWidth={1.5} />
        </motion.div>
      </section>

      <div className="mt-10 sm:mt-14">
        <Marquee />
      </div>

      {/* ————— CATEGORY TILES ————— */}
      <section className="py-14 lg:py-20" data-testid="home-categories">
        <div className="max-w-[1360px] mx-auto px-3 sm:px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {cats.map((c, i) => (
            <Reveal key={c} delay={i * 0.07}>
              <Link to={`/boutique?category=${c}`} data-testid={`home-category-${c}`} className="group block">
                <div className="overflow-hidden rounded-[1.5rem] sm:rounded-[2rem]">
                  <div className="transition-transform duration-700 group-hover:scale-[1.03]">
                    <Placeholder variant={(i * 2 + 1) % 6} aspect="aspect-[4/5]" />
                  </div>
                </div>
                <p className="mt-3 sm:mt-4 text-center text-[15px] sm:text-lg text-ink font-medium group-hover:text-rose-deep transition-colors">
                  {CATEGORY_META[c][lang]}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ————— LA SÉLECTION — product videos ————— */}
      <section className="py-14 lg:py-20 border-t border-ink/10" data-testid="home-selection">
        <Reveal className="text-center mb-10 lg:mb-14 px-5">
          <p className="eyebrow mb-3">@mioraboutique_</p>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-ink">{t.home.selection.title}</h2>
          <p className="mt-4 text-ink/55 text-sm">{t.home.selection.sub}</p>
        </Reveal>
        <div className="relative max-w-[1440px] mx-auto">
          <div
            ref={scroller}
            data-lenis-prevent
            className="flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar px-3 sm:px-5 lg:px-10 pb-2"
            data-testid="home-video-scroller"
          >
            {videoTiles.map((p, i) => (
              <div key={p.slug} className="w-[70%] sm:w-[42%] lg:w-[27%] shrink-0 snap-start">
                <Link to={`/piece/${p.slug}`} data-testid={`home-video-tile-${i}`} className="group block">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-[1.5rem] sm:rounded-[2rem]">
                    <div className="absolute inset-0 ph">
                      <div className="absolute inset-0" style={{ backgroundImage: GRADIENTS[p.gradient % 6] }} />
                    </div>
                    <div className="absolute inset-0 bg-ink/20 transition-colors duration-300 group-hover:bg-ink/35" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-14 h-14 rounded-full bg-ivory/90 flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(44,36,32,0.5)] transition-transform duration-300 group-hover:scale-110">
                        <Play className="w-5 h-5 text-rose-deep fill-rose-deep translate-x-[1px]" />
                      </span>
                    </span>
                    <span className="ph-label">{t.common.videoPending}</span>
                    <span className="absolute bottom-3 end-3 z-10 text-ivory text-[11px] uppercase tracking-[0.1em] bg-ink/45 backdrop-blur px-3 py-1.5 rounded-full">
                      {p.name[lang]}
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          <button
            onClick={scrollVideos}
            data-testid="home-video-arrow"
            aria-label="Scroll"
            className="absolute top-[42%] end-5 sm:end-8 lg:end-14 w-12 h-12 rounded-2xl bg-ivory border border-ink/15 shadow-[0_10px_30px_-12px_rgba(44,36,32,0.35)] flex items-center justify-center text-ink hover:bg-ink hover:text-ivory transition-all duration-300"
          >
            <ArrowRight className="w-5 h-5 rtl:rotate-180" strokeWidth={1.5} />
          </button>
        </div>
      </section>

      {/* ————— NEW IN GRID ————— */}
      <section className="py-14 lg:py-20 border-t border-ink/10" data-testid="home-new-in">
        <div className="max-w-[1360px] mx-auto px-3 sm:px-5 lg:px-8">
          <Reveal className="text-center mb-12">
            <p className="eyebrow mb-3">{t.home.newIn.eyebrow}</p>
            <h2 className="font-serif font-light text-3xl lg:text-[2.75rem] text-ink">{t.home.newIn.title}</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-12">
            {grid.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/boutique" className="btn btn-outline" data-testid="home-new-in-cta">{t.home.newIn.cta}</Link>
          </div>
        </div>
      </section>

      {/* ————— INSTAGRAM ————— */}
      <section className="py-14 lg:py-20" data-testid="home-instagram">
        <div className="max-w-3xl mx-auto px-3 sm:px-5">
          <Reveal className="text-center mb-8">
            <p className="eyebrow mb-3">@mioraboutique_</p>
            <h2 className="font-serif font-light text-3xl text-ink">{t.home.instagram.title}</h2>
          </Reveal>
          <div className="grid grid-cols-3 gap-3">
            {products.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06}>
                <a href="https://instagram.com/mioraboutique_" target="_blank" rel="noreferrer" data-testid={`home-instagram-tile-${i}`} className="block group">
                  <div className="overflow-hidden rounded-2xl">
                    <div className="transition-transform duration-700 group-hover:scale-[1.04]">
                      <Placeholder variant={(i + 2) % 6} aspect="aspect-square" label="" />
                    </div>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— FAQ ON HOME ————— */}
      <section className="py-16 lg:py-24 border-t border-ink/10" data-testid="home-faq">
        <div className="max-w-2xl mx-auto px-5 lg:px-10">
          <Reveal className="text-center mb-10">
            <h2 className="font-serif font-light text-3xl lg:text-[2.5rem] text-ink">{t.home.homeFaq.title}</h2>
            <p className="mt-3 text-ink/55 text-sm">{t.home.homeFaq.sub}</p>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible>
              {homeFaq.map(({ cat, item }, i) => (
                <AccordionItem key={cat} value={cat} className="border-ink/10">
                  <AccordionTrigger className="text-start text-[14.5px] text-ink hover:text-rose-deep hover:no-underline" data-testid={`home-faq-trigger-${i}`}>
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-ink/60 text-sm leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="text-center mt-8">
              <Link to="/faq" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] text-rose-deep hover:text-ink transition-colors" data-testid="home-faq-cta">
                {t.home.homeFaq.cta}
                <ArrowRight className="w-4 h-4 rtl:rotate-180" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————— NEWSLETTER ————— */}
      <section className="py-16 lg:py-24 border-t border-ink/10" data-testid="home-newsletter">
        <div className="max-w-xl mx-auto px-5 lg:px-10 text-center">
          <Reveal>
            <h2 className="font-serif font-light text-3xl lg:text-4xl text-ink">{t.home.newsletter.title}</h2>
            <p className="mt-4 text-ink/55 text-sm">{t.home.newsletter.sub}</p>
            <form onSubmit={subscribe} className="mt-9 flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.home.newsletter.ph}
                className="field-line flex-1 text-start"
                data-testid="newsletter-input"
                aria-label={t.home.newsletter.ph}
              />
              <button type="submit" disabled={subscribing} className="btn btn-ink !px-8" data-testid="newsletter-submit">
                {t.home.newsletter.cta}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
